import { motion } from 'framer-motion'
import { useInView } from 'framer-motion'
import { useRef } from 'react'
import type { Services as ServicesType } from '../types/content'
import { Briefcase, Search, Settings, Check } from './Icons'

interface ServicesProps {
  data: ServicesType
}

const iconMap: Record<string, React.ReactNode> = {
  briefcase: <Briefcase className="w-8 h-8" />,
  search: <Search className="w-8 h-8" />,
  settings: <Settings className="w-8 h-8" />,
}

export default function Services({ data }: ServicesProps) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  return (
    <section id="services" className="section" ref={ref}>
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="section-header"
        >
          <h2 className="section-title text-gradient">{data.title}</h2>
          <p className="section-subtitle">{data.subtitle}</p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {data.services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
              className="card-gradient group"
            >
              <div className="icon-box icon-box-md mb-6 group-hover:shadow-glow transition-shadow">
                {iconMap[service.icon] || <Briefcase className="w-8 h-8" />}
              </div>

              <h3 className="text-xl font-semibold mb-3 group-hover:text-gradient transition-all">
                {service.title}
              </h3>
              <p className="text-gray-400 mb-6">{service.description}</p>

              <ul className="space-y-2">
                {service.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-400">
                    <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
